import { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  useGetCourseBySlugQuery,
  useGetCourseResultQuery,
} from "@/features/api/courseApi";

import {
  User,
  Award,
  History,
  Loader2,
  BarChart3,
  Trophy,
  TrendingUp,
  AlertTriangle,
  Clock,
  RotateCcw
} from "lucide-react";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

/* ================= COMPONENT ================= */

export default function CourseResult() {
  const { slug } = useParams();
  const navigate = useNavigate();

  /* ================= COURSE ================= */

  const {
    data: courseRes,
    isLoading: courseLoading,
    error: courseError
  } = useGetCourseBySlugQuery(slug, { skip: !slug });

  const course = courseRes?.course;
  const PASS_PERCENT = course?.passPercentage || 30;

  /* ================= RESULT ================= */

  const {
    data: rawResult,
    isLoading: resultLoading,
    error: resultError
  } = useGetCourseResultQuery(course?._id, {
    skip: !course?._id,
    refetchOnMountOrArgChange: true,
  });

  /* ================= NORMALIZE ================= */

  const { results, user } = useMemo(() => {
    try {
      const progress = rawResult?.progress;

      if (!progress) return { results: [], user: {} };

      const lessons = Array.isArray(progress?.lessonsProgress)
        ? progress.lessonsProgress
        : [];

      return {
        results: lessons.map((lp, i) => ({
          _id: lp?.lessonId || i,
          scorePercent: Number(lp?.score ?? 0) || 0,
          lessonId: lp?.lessonId || null,
        })),
        user:
          typeof progress?.user === "object"
            ? progress.user
            : {},
      };
    } catch (e) {
      console.error("Normalize error:", e);
      return { results: [], user: {} };
    }
  }, [rawResult]);

  /* ================= STATS ================= */

  const stats = useMemo(() => {
    if (!results.length) {
      return { attempts: 0, best: 0, avg: 0, latest: 0 };
    }

    const arr = results.map(r => Number(r.scorePercent || 0));

    return {
      attempts: arr.length,
      best: Math.max(...arr),
      avg: Math.round(arr.reduce((a, b) => a + b, 0) / arr.length),
      latest: arr[arr.length - 1],
    };
  }, [results]);

  /* ================= LESSON MAP ================= */

  const lessonMap = useMemo(() => {
    if (!course?.lessons) return {};
    return Object.fromEntries(
      course.lessons.map(l => [l._id, l.title])
    );
  }, [course]);

  /* ================= GRAPH ================= */

  const chartData = useMemo(() => {
    if (!results.length) return [];

    return results.map((r, i) => ({
      attempt: lessonMap[r.lessonId] || `Lesson ${i + 1}`,
      score: Number(r.scorePercent || 0),
    }));
  }, [results, lessonMap]);

  /* ================= WEAK ================= */

  const weakTopics = useMemo(() => {
    return results
      .filter(r => r.scorePercent < 50)
      .map(r => lessonMap[r.lessonId] || "Lesson")
      .slice(0, 3);
  }, [results, lessonMap]);

  /* ================= TIME ================= */

  const totalTime = useMemo(() => {
    return rawResult?.progress?.lessonsProgress?.reduce(
      (sum, l) => sum + Number(l?.timeSpent || 0),
      0
    ) || 0;
  }, [rawResult]);

  /* ================= INSIGHTS ================= */

  const insights = useMemo(() => {
    const msg = [];

    if (stats.latest < PASS_PERCENT) {
      msg.push("You need improvement to pass.");
    }

    if (stats.avg < 50) {
      msg.push("Focus on fundamentals.");
    }

    if (!msg.length) {
      msg.push("Great performance 🚀");
    }

    return msg;
  }, [stats, PASS_PERCENT]);

  /* ================= STATES ================= */

  if (courseLoading || resultLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  if (courseError || resultError) {
    return (
      <div className="text-center py-20 text-red-600">
        Failed to load results
      </div>
    );
  }

  if (!course) {
    return <div className="text-center py-20">Course not found</div>;
  }

  if (!results.length) {
    return (
      <div className="text-center py-20">
        <p className="mb-4">No results yet</p>
        <button
          onClick={() => navigate(`/courses/${slug}/learn`)}
          className="bg-blue-600 text-white px-6 py-2 rounded"
        >
          Start Learning
        </button>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-6xl mx-auto space-y-8">

        <h1 className="text-3xl font-bold text-center">
          {course.title}
        </h1>

        {/* STATS */}
        <div className="grid md:grid-cols-5 gap-4">
          <Stat icon={Trophy} label="Best" value={`${stats.best}%`} />
          <Stat icon={BarChart3} label="Average" value={`${stats.avg}%`} />
          <Stat icon={History} label="Lessons" value={stats.attempts} />
          <Stat icon={Award} label="Latest" value={`${stats.latest}%`} />
          <Stat icon={Clock} label="Time" value={`${Math.floor(totalTime / 60)}m`} />
        </div>

        {/* GRAPH */}
        {chartData.length > 0 && (
          <Card title="Progress" icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="attempt" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="score" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* WEAK */}
        <Card title="Weak Areas" icon={AlertTriangle}>
          {weakTopics.length
            ? weakTopics.map((t, i) => <p key={i}>{t}</p>)
            : "None 🎉"}
        </Card>

        {/* INSIGHTS */}
        <Card title="Insights" icon={TrendingUp}>
          {insights.map((i, idx) => <p key={idx}>• {i}</p>)}
        </Card>

        {/* USER */}
        <Card title="Student" icon={User}>
          <p>{user?.name || "N/A"}</p>
          <p>{user?.email || ""}</p>
        </Card>

        {/* ACTION */}
        <div className="text-center">
          <button
            onClick={() => navigate(`/courses/${slug}/learn`)}
            className="bg-blue-600 text-white px-6 py-2 rounded flex items-center gap-2 mx-auto"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Course
          </button>
        </div>

      </div>
    </div>
  );
}

/* ================= UI ================= */

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white p-4 rounded shadow text-center">
      <Icon className="mx-auto mb-2 text-blue-600" />
      <p className="text-sm">{label}</p>
      <p className="text-xl font-bold">{value}</p>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white p-6 rounded shadow">
      <h2 className="flex items-center gap-2 font-semibold mb-4">
        <Icon size={18} className="text-blue-600" />
        {title}
      </h2>
      {children}
    </div>
  );
}